import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import CloseThin from '@wix/da-ds/pkg/Icons/generated/components/CloseThin';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import SearchIcon from '@wix/da-ds/pkg/Icons/24x24/Search';
import {
  BiData,
  SearchClickBiEvent,
  TopNavClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';

import s from './SearchModal.scss';

const USERNAME_REGEX = /^[A-Za-z0-9][A-Za-z0-9-]{1,18}[A-Za-z0-9]$/;

export interface Props {
  onClose: () => void;
  searchQuery?: string;
}

const SearchModal: React.FC<Props> = ({ onClose, searchQuery }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const [inputValue, setInputValue] = useState(searchQuery ?? '');
  const logBiEvent = useBiLogger();
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const searchTerm = searchInputRef.current?.value?.trim();

      if (!searchTerm) {
        return; // dont allow empty search
      }

      const searchUrl = Url.searchLink(searchTerm);
      const username = searchTerm.slice(1);
      const isCorrectUsername =
        searchTerm.startsWith('@') && USERNAME_REGEX.test(username);

      logBiEvent(
        BiData<SearchClickBiEvent>({
          evid: 710,
          search_terms: searchTerm,
          sectionname: 'popup',
        })
      );

      // Only search usernames if we are not on subdomain!
      if (isCorrectUsername) {
        const userLinkUrl = Url.userLink(username);
        // to test this locally do `.replace('.com', '.lan')`

        const http = new XMLHttpRequest();
        http.open('HEAD', userLinkUrl);
        http.onreadystatechange = function () {
          if (this.readyState === this.DONE) {
            const doesUserExists = this.status === 200;
            const redirectUrl = doesUserExists ? userLinkUrl : searchUrl;
            window.location.assign(redirectUrl);
          }
        };
        http.send();
      } else {
        window.location.assign(searchUrl);
      }
    },
    [logBiEvent]
  );

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === 'Return') {
        event.preventDefault();
        event.stopPropagation();
        handleSubmit(event);
      }
    },
    [handleSubmit]
  );

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  return (
    <Modal
      overlayClassName={s['search-popup']}
      className={classnames(s['search-popup-content'], s['updated-inputs'])}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      onRequestClose={onClose}
      isOpen={true}
    >
      <div>
        <div
          className={s['search-popup-form']}
          onClick={e => e.stopPropagation()}
        >
          <form
            className={s['search-form']}
            action={Url.searchLink()}
            onSubmit={handleSubmit}
          >
            <TextInput
              ref={searchInputRef}
              id="search-input"
              label={t('siteHeader.searchBar.placeholder')}
              placeholder={t('siteHeader.searchBar.placeholder')}
              value={inputValue}
              onChange={e => handleChange(e.target.value)}
              onKeyDown={handleInputKeyDown}
              postfix={
                <PlainButtonOrLink
                  onClick={handleSubmit}
                  biData={
                    inputValue.length === 0
                      ? BiData<TopNavClickBiEvent>({
                          evid: 700,
                          link_name: 'search',
                        })
                      : undefined
                  }
                >
                  <SearchIcon disableDefaultColors />
                </PlainButtonOrLink>
              }
              className={s['search-popup-input']}
              noBottomSpacing
              isLabelHidden
              autoFocus
              biData={BiData<TopNavClickBiEvent>({
                evid: 700,
                link_name: 'search',
              })}
            />
          </form>
          <button className={s['search-popup-close-button']} onClick={onClose}>
            <CloseThin />
          </button>
        </div>
        {isMobile && (
          <div className={s['search-suggestions']}>
            <h3>{t('siteHeader.search.suggestions')}</h3>
            {[
              'Photography',
              'Body Art',
              'Artisan Designers',
              'Fashion',
              'Literature',
            ].map(i => (
              <a key={i} href={Url.searchLink(i.toLowerCase())}>
                {i}
              </a>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};
SearchModal.displayName = 'SearchModal';

export default SearchModal;
